/**
 * Spatial audio wrapper — PannerNode per-play with AudioListener position.
 *
 * Docs verified (W3C Web Audio API spec, webaudio.github.io/web-audio-api):
 * - PannerNode.panningModel: 'equalpower' (cheap 2D panning) | 'HRTF' (binaural, deferred)
 * - PannerNode.positionX/Y/Z: readonly AudioParam — use setValueAtTime(); setPosition() is deprecated
 * - AudioListener.positionX/Y/Z: readonly AudioParam — same modern API pattern
 */

import type { Vec3, ListenerTransformProvider } from './types';

export interface SpatialEmitter {
  /** Construct a fresh PannerNode at world `position`, connect it → sfxGain, return as AudioNode. */
  createPannerForPosition(position: Vec3): AudioNode;
  /** Sync AudioContext listener position from getListenerTransform(). No-op if result is null. */
  updateListener(): void;
}

export interface SpatialEmitterDeps {
  readonly audioContext: AudioContext;
  readonly sfxGain: GainNode;
  readonly getListenerTransform: ListenerTransformProvider;
}

export function createSpatialEmitter(deps: SpatialEmitterDeps): SpatialEmitter {
  const { audioContext, sfxGain, getListenerTransform } = deps;

  function createPannerForPosition(position: Vec3): AudioNode {
    const panner = audioContext.createPanner();

    // Panning model: equalpower is cheaper than HRTF (no convolution kernel).
    // HRTF deferred to a future plan.
    panner.panningModel = 'equalpower';

    // Distance model defaults — set explicitly to guard against UA default drift.
    // W3C defaults: distanceModel='inverse', refDistance=1, maxDistance=10000, rolloffFactor=1
    // We override maxDistance to 100 per spec §829 for game scale.
    panner.distanceModel = 'inverse';
    panner.refDistance = 1;
    panner.maxDistance = 100;
    panner.rolloffFactor = 1;

    // Set position via modern AudioParam API (setValueAtTime), not deprecated setPosition().
    const t = audioContext.currentTime;
    panner.positionX.setValueAtTime(position.x, t);
    panner.positionY.setValueAtTime(position.y, t);
    panner.positionZ.setValueAtTime(position.z, t);

    // Route panner output through the SFX gain channel.
    panner.connect(sfxGain);

    return panner as AudioNode;
  }

  function updateListener(): void {
    const transform = getListenerTransform();
    if (transform === null) return;

    // Forward vector ignored in equalpower mode (no orientation processing).
    // Reserved for HRTF binaural mode when that is enabled.
    const t = audioContext.currentTime;
    audioContext.listener.positionX.setValueAtTime(transform.position.x, t);
    audioContext.listener.positionY.setValueAtTime(transform.position.y, t);
    audioContext.listener.positionZ.setValueAtTime(transform.position.z, t);
  }

  return { createPannerForPosition, updateListener };
}
